import {
  Check,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Loader2,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DecisionStrip, type DecisionStripBefore } from "@/components/underwriting/DecisionStrip";
import { useCorrectionsPreviewQuery } from "@/hooks/queries/useAnalysisRuns";
import { cn } from "@/lib/utils";
import type { CorrectionImpact, CorrectionItem } from "@/types/analysisRun";

import { agentClient } from "../agentClient";
import type { ChoiceBlocks, TickLabel, WireChoice } from "../protocol/protocol";
import { AssistantCardFooter, type AssistantCardTone } from "../theme";
import { DECISION_NAMES, moves, NONE } from "./transactionChange";
import { choiceOptions, type ChoiceOutcome } from "./wire";

/**
 * What the analysis correction cards share (reclassify_transactions, correct_position): how a
 * card looks as it is decided, its row ticks, the analysis's top row before and after (worked out
 * again for the ticked rows), and its buttons.
 */

export const APPROVE = "approve";

export const LOOKS: Record<
  ChoiceOutcome,
  { icon: LucideIcon; tone: AssistantCardTone; receipt: string | null }
> = {
  pending: { icon: Shield, tone: "primary", receipt: null },
  approved: { icon: Check, tone: "success", receipt: "Approved" },
  declined: { icon: CircleX, tone: "muted", receipt: "Declined · nothing changed" },
  superseded: { icon: Clock, tone: "muted", receipt: "Superseded · nothing changed" },
};

export const ROW_STATES = {
  running: { icon: Loader2, className: "animate-spin text-primary", label: "Running" },
  done: { icon: CircleCheck, className: "text-success", label: "Done" },
  failed: { icon: CircleX, className: "text-destructive", label: "Failed" },
  skipped: { icon: CircleDashed, className: "text-muted-foreground", label: "Skipped" },
} as const;

export type Stats = NonNullable<ChoiceBlocks["stats"]>;

// The card's figures are usually the application alone: it takes the row, not a column of four
// (FigureGrid's four columns come by its own width, so the override takes that variant too).
export const FIGURES_FILL_ROW =
  "grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] [@container(min-width:28rem)]:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]";

export const dollars = (value: string) =>
  value ? `$${Math.round(Number(value)).toLocaleString("en-US")}` : NONE;
export const sameKeys = (a: Set<string>, b: string[]) =>
  a.size === b.length && b.every((k) => a.has(k));

export function TickBox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean | "indeterminate";
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <Checkbox checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={label} />
  );
}

/** A decided row's outcome as an icon, or its tick while the card is open. */
export function RowMark({
  state,
  checked,
  onChange,
  label,
  disabled,
}: {
  state: keyof typeof ROW_STATES | null | undefined;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled: boolean;
}) {
  if (state) {
    const { icon: Icon, className, label: name } = ROW_STATES[state];
    return <Icon className={cn("h-4 w-4", className)} aria-label={name} role="img" />;
  }
  return <TickBox checked={checked} onChange={onChange} label={label} disabled={disabled} />;
}

export function approveLabel(
  label: string,
  tickLabel: TickLabel | null | undefined,
  count: number
) {
  if (!tickLabel) return label;
  return `${tickLabel.verb} ${count} ${count === 1 ? tickLabel.one : tickLabel.many}`;
}

/**
 * The ticks on a card's rows (`ticks` null while they are as proposed). A row the call no longer
 * has (the card reopened after an approve failed) can't be ticked, nor stay ticked here when
 * another client's narrowed approve left it out: `ticked` is only ever offered rows.
 */
export function useTicks(
  rows: readonly { key: string; ticked: boolean; disabled: string }[],
  pending: boolean
) {
  const [ticks, setTicks] = useState<Set<string> | null>(null);
  const proposed = rows.filter((row) => row.ticked).map((row) => row.key);
  const offered = rows.filter((row) => !row.disabled);
  const ticked = new Set(
    offered.filter((row) => (ticks ? ticks.has(row.key) : row.ticked)).map((row) => row.key)
  );
  const narrowed = pending && ticks !== null && !sameKeys(ticked, proposed);
  const toggle = (key: string) => {
    const next = new Set(ticked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setTicks(next);
  };
  return { proposed, offered, ticked, narrowed, toggle, setTicks };
}

/** Pressing a card's option, with the ticked rows when they differ from the proposal. */
export function useDecide(choiceRef: string) {
  const [pressed, setPressed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const press = (optionId: string, keys?: string[]) => {
    if (pressed !== null) return;
    setPressed(optionId);
    setError(null);
    agentClient
      .decide(choiceRef, optionId, undefined, optionId === APPROVE ? keys : undefined)
      .catch((reason: { message?: string }) => {
        setError(reason?.message || "That didn't go through. Try again.");
      })
      .finally(() => setPressed(null));
  };
  return { pressed, busy: pressed !== null, error, setError, press };
}

/** The proposal's figures with nothing changing: what approving no rows would do. */
function unchanged(stats: Stats): Stats {
  const stay = <T extends { old: string; new: string }>(figure: T | undefined) =>
    figure && { ...figure, new: figure.old };
  return {
    ...stats,
    recent_revenue: stats.recent_revenue.map((month) => ({ ...month, new: month.old })),
    average_revenue: stay(stats.average_revenue),
    leverage: stay(stats.leverage),
    mca_payout: stay(stats.mca_payout),
    offer: stay(stats.offer),
  };
}

/**
 * The figures for what would be approved: the proposal's (the server's for the approved rows once
 * decided), the ticked rows' once they differ (asked of the server, `items`), and none changing
 * with nothing ticked. When the ticked rows' can't be worked out, the proposal's (`failed`).
 * `updating` while they aren't yet for these ticks (the debounce, the request, the last answer).
 */
export function useCorrectionImpact(
  card: { applicationId: number; source: "statements" | "plaid"; stats: Stats | null } | null,
  {
    narrowed,
    nothingTicked,
    items,
  }: { narrowed: boolean; nothingTicked: boolean; items: CorrectionItem[] }
) {
  // Only when there are figures to update (none during a cadence outage: it would fail the same).
  const previewing = narrowed && !nothingTicked && !!card?.stats;
  const preview = useCorrectionsPreviewQuery(
    previewing ? (card?.applicationId ?? null) : null,
    items,
    card?.source
  );
  const stats = card?.stats ?? null;
  const failed = previewing && preview.isError;
  const updating = previewing && !failed && !preview.current;
  const impact: Stats | CorrectionImpact | null =
    !narrowed || !stats || failed
      ? stats
      : nothingTicked
        ? unchanged(stats)
        : (preview.data?.impact ?? stats);
  return { impact, failed, updating };
}

/** What the figures are for, beside the Impact heading. */
export function impactScope({
  outcome,
  narrowed,
  failed,
  chosen,
  proposed,
  offered,
  stats,
}: {
  outcome: ChoiceOutcome;
  narrowed: boolean;
  failed: boolean;
  chosen: number;
  proposed: number;
  offered: number;
  stats: Stats | null;
}) {
  if (narrowed) return failed ? `for all ${proposed}` : `for the ${chosen} selected`;
  if (outcome === "approved" && stats?.scope === "proposed")
    return `as proposed for all ${offered}`;
  if (outcome === "approved" && proposed < offered) return `for the ${proposed} approved`;
  if (outcome === "declined" || outcome === "superseded")
    return `as proposed for all ${proposed}, not applied`;
  return `for all ${proposed}`;
}

/** The top row's figures after the change, and what they were. */
function stripFigures(impact: Stats | CorrectionImpact) {
  const lines = (side: "old" | "new") =>
    impact.recent_revenue.map((month) => ({
      month_abbrev: month.label,
      amount: Number(month[side] || 0),
      amount_display: dollars(month[side]),
    }));
  const average = impact.average_revenue;
  const before: DecisionStripBefore = {
    revenueLines: lines("old"),
    avgRevenue: average ? dollars(average.old) : null,
    leveragePct: Number(impact.leverage?.old || 0),
    monthlyPayout: Number(impact.mca_payout?.old || 0),
  };
  return {
    revenueLines: lines("new"),
    avgRevenue: average ? dollars(average.new) : null,
    leveragePct: Number(impact.leverage?.new || 0),
    targetPct: Number(impact.leverage?.target || 0),
    monthlyPayout: Number(impact.mca_payout?.new || 0),
    before,
  };
}

/**
 * The Impact section: the analysis's top row as it would be, over what it was, then the offer and
 * the decision when they move. `months` makes the revenue months open a panel (`wrapStrip` places
 * it around the strip).
 */
export function ImpactSection({
  group,
  impact,
  scope,
  updating,
  dimmed,
  hideDecision,
  failedNote,
  months,
  wrapStrip = (strip) => strip,
  children,
}: {
  group: string | undefined;
  impact: Stats | CorrectionImpact | null;
  scope: string;
  updating: boolean;
  /** The figures aren't what the analysis shows (declined, superseded). */
  dimmed: boolean;
  hideDecision: boolean;
  /** Said under the figures when the ticked rows' couldn't be worked out. */
  failedNote: string | null;
  months?: { active: string | null; onClick: (month: string) => void };
  wrapStrip?: (strip: ReactNode) => ReactNode;
  children?: ReactNode;
}) {
  if (!impact) return null;
  const strip = stripFigures(impact);
  const offer = impact.offer && moves(impact.offer) ? impact.offer : null;
  const decision =
    impact.decision?.new && moves(impact.decision) && !hideDecision ? impact.decision : null;
  return (
    <section aria-label="Impact" className="flex flex-col gap-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {group ?? "Impact"}
        <span className="font-normal normal-case tracking-normal">{scope}</span>
        {updating && <Loader2 className="h-3 w-3 animate-spin" aria-label="Updating" />}
      </p>
      <div
        aria-busy={updating || undefined}
        className={cn(
          "flex flex-col gap-1.5 transition-opacity",
          (updating || dimmed) && "opacity-50"
        )}
      >
        {wrapStrip(
          <div>
            <DecisionStrip
              revenueLines={strip.revenueLines}
              avgRevenue={strip.avgRevenue}
              leveragePct={strip.leveragePct}
              targetPct={strip.targetPct}
              monthlyPayout={strip.monthlyPayout}
              before={strip.before}
              activeMonth={months?.active}
              onMonthClick={months?.onClick}
            />
          </div>
        )}
        {children}
        {offer && (
          <p className="text-xs text-muted-foreground">
            {offer.label}: <s>{dollars(offer.old)}</s> → {dollars(offer.new)}
          </p>
        )}
        {decision && (
          <p className="text-xs">
            <span className="text-muted-foreground">{decision.label}: </span>
            <s className="text-muted-foreground">
              {DECISION_NAMES[decision.old] ?? (decision.old || NONE)}
            </s>{" "}
            → <strong>{DECISION_NAMES[decision.new] ?? decision.new}</strong>
          </p>
        )}
      </div>
      {failedNote && <p className="text-xs text-muted-foreground">{failedNote}</p>}
    </section>
  );
}

/**
 * The card's buttons while it is open (approve relabelled for the ticked rows, and not pressable
 * with none), else how it went.
 */
export function DecisionFooter({
  choice,
  outcome,
  pressed,
  chosen,
  narrowed,
  onPress,
}: {
  choice: WireChoice;
  outcome: ChoiceOutcome;
  pressed: string | null;
  chosen: number;
  narrowed: boolean;
  onPress: (optionId: string) => void;
}) {
  if (outcome !== "pending") {
    const succeeded =
      outcome === "approved" && choice.executed && typeof choice.activity?.succeeded === "string"
        ? choice.activity.succeeded
        : null;
    return (
      <AssistantCardFooter
        role="status"
        className={cn("text-xs", outcome === "approved" ? "text-success" : "text-muted-foreground")}
      >
        {succeeded ?? LOOKS[outcome].receipt}
      </AssistantCardFooter>
    );
  }
  return (
    <AssistantCardFooter className="flex-wrap max-sm:[&>button]:flex-1">
      {choiceOptions(choice).map((option) => {
        const approve = option.id === APPROVE;
        return (
          <Button
            key={option.id}
            type="button"
            variant={approve ? "default" : "outline"}
            aria-disabled={pressed !== null}
            disabled={approve && !chosen}
            onClick={() => onPress(option.id)}
          >
            {pressed === option.id && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {approve && narrowed && chosen
              ? approveLabel(option.label, choice.tick_label, chosen)
              : option.label}
          </Button>
        );
      })}
    </AssistantCardFooter>
  );
}
